import React, { useEffect, useRef, useState } from 'react';
import GoogleMapReactMarker, { Map } from 'google-maps-react-markers';
import { DEFAULT_FALLBACK_LOCATION } from 'constants/geoBlocking';
import { useGeoLocation } from 'hooks/useGeoLocation';
import Skeleton from 'components/LoadingSkeleton';
import Marker from './Marker';
import { GoogleMapProps } from './props';
import { MapContainer } from './styles';

// Fallback centre for soft-blocked users.
const FALLBACK_LAT = DEFAULT_FALLBACK_LOCATION.position.latitude;
const FALLBACK_LNG = DEFAULT_FALLBACK_LOCATION.position.longitude;

/**
 * Check if Google Maps is loaded and ready.
 * Waits for the global Script to load Google Maps before rendering the map component.
 * This prevents race conditions where the map component tries to load Google Maps
 * independently while our Script is also loading it.
 */
const useGoogleMapsAvailability = () => {
  // Always start with false to avoid hydration mismatch (server has no window)
  const [isReady, setIsReady] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    // Check if blocked by geo-blocking
    if ((window as any).__googleMapsBlocked) {
      setIsBlocked(true);
      return;
    }

    // Check if already loaded (Map constructor is key for rendering)
    if (window.google?.maps?.Map) {
      setIsReady(true);
      return;
    }

    // Wait for Google Maps to load (max 15 seconds)
    let attempts = 0;
    const maxAttempts = 150;
    const interval = setInterval(() => {
      attempts++;

      if ((window as any).__googleMapsBlocked) {
        setIsBlocked(true);
        clearInterval(interval);
        return;
      }

      // Check for Map constructor specifically - this is what the library needs
      if (window.google?.maps?.Map) {
        setIsReady(true);
        clearInterval(interval);
        return;
      }

      if ((window as any).__googleMapsError) {
        if (process.env.APP_STAGE !== 'production') {
          console.error(
            '[GoogleMap] Google Maps failed to load:',
            (window as any).__googleMapsError,
          );
        }
        // Still try to render - library might handle it
        setIsReady(true);
        clearInterval(interval);
        return;
      }

      if (attempts >= maxAttempts) {
        if (process.env.APP_STAGE !== 'production') {
          console.warn('[GoogleMap] Timeout waiting for Google Maps, attempting render anyway');
        }
        setIsReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return { isReady, isBlocked };
};

const GoogleMap: React.FC<GoogleMapProps> = ({
  defaultCenter,
  center,
  zoom = 12,
  markers = [],
  mapId,
  options = {},
  onChange,
  onGoogleApiLoaded,
  onCenterChange,
  isExactCenter = false,
  centerIcon,
  syncDragToURL = false,
  softBlockPlaceholder = false,
}) => {
  const mapRef = useRef<Map | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const { onMapDrag } = useGeoLocation();
  const { isReady, isBlocked } = useGoogleMapsAvailability();

  const handleGoogleApiLoaded = ({ map, maps }: { map: Map; maps: typeof google.maps }) => {
    mapRef.current = map;

    setupMapEventListeners(map);

    onGoogleApiLoaded?.(map, maps);
  };

  const setupMapEventListeners = (map: Map) => {
    cleanupEventListeners();

    if (!syncDragToURL && !onCenterChange) {
      return;
    }

    const handleMapCenterChange = () => {
      const currentCenter = map.getCenter();
      if (!currentCenter) {
        return;
      }

      const centerCoords = {
        lat: currentCenter.lat(),
        lng: currentCenter.lng(),
      };

      onCenterChange?.(centerCoords);

      if (syncDragToURL) {
        onMapDrag({
          latitude: centerCoords.lat,
          longitude: centerCoords.lng,
        });
      }
    };

    const dragListener = map.addListener('dragend', handleMapCenterChange);
    const zoomListener = map.addListener('zoom_changed', handleMapCenterChange);

    listenersRef.current = [dragListener, zoomListener];
  };

  /**
   * Cleans up all map event listeners
   */
  const cleanupEventListeners = () => {
    listenersRef.current.forEach((listener) => {
      google.maps.event.removeListener(listener);
    });
    listenersRef.current = [];
  };

  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.panTo({ lat: center.lat, lng: center.lng });
    }
  }, [center?.lat, center?.lng]);

  useEffect(() => {
    return () => {
      cleanupEventListeners();
    };
  }, []);

  const mapCenter = center || defaultCenter || { lat: 0, lng: 0 };

  // Show fallback for hard-blocked users (no Google Maps loaded at all)
  if (isBlocked) {
    return (
      <MapContainer>
        <div className="flex h-full w-full items-center justify-center bg-gray-100">
          <p className="text-gray-500">Map unavailable in your region</p>
        </div>
      </MapContainer>
    );
  }

  // Soft block: show placeholder until user searches for an address
  // This saves map render costs (~$7/1000) for suspected bots who never search
  // Only show placeholder if center is still the fallback (user hasn't searched yet)
  const isStillAtDefault =
    Math.abs(mapCenter.lat - FALLBACK_LAT) < 0.001 &&
    Math.abs(mapCenter.lng - FALLBACK_LNG) < 0.001;

  if (softBlockPlaceholder && isStillAtDefault) {
    return (
      <MapContainer>
        <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100">
          <svg
            className="mb-4 h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
            />
          </svg>
          <p className="text-center text-gray-600">Enter your address to search near you</p>
        </div>
      </MapContainer>
    );
  }

  // Show loading state while waiting for Google Maps
  if (!isReady) {
    return (
      <MapContainer>
        <Skeleton className="h-full w-full" count={10} />
      </MapContainer>
    );
  }

  return (
    <MapContainer>
      <GoogleMapReactMarker
        apiKey={process.env.GOOGLE_MAPS_API_KEY as string}
        defaultCenter={mapCenter}
        defaultZoom={zoom}
        options={{ gestureHandling: 'greedy', ...options, mapId }}
        onChange={onChange}
        onGoogleApiLoaded={handleGoogleApiLoaded}
        loadingContent={<Skeleton className="h-full w-full" count={10} />}
      >
        {markers.map((marker, index) => (
          <Marker key={index} {...marker} />
        ))}
        {isExactCenter && centerIcon && (
          <Marker
            lat={mapCenter.lat}
            lng={mapCenter.lng}
            className="transition-all duration-300 ease-in-out"
          >
            {centerIcon}
          </Marker>
        )}
      </GoogleMapReactMarker>
    </MapContainer>
  );
};

export default GoogleMap;
